import { useCallback, useRef, useState } from 'react';
import { getAllEntreprises } from '../../servicesV2/entrepriseService';
import { getAllClientRequests, getAllPartners } from '../../servicesV2/partnerService';

/**
 * useEntityCommands — lazily loads clients, partners, and client requests
 * the first time the command palette opens, then caches for the session.
 *
 * The palette calls `ensureLoaded()` on open. Subsequent opens are instant.
 */
export function useEntityCommands() {
  const [clients, setClients] = useState([]);
  const [partners, setPartners] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const inflight = useRef(null);

  const ensureLoaded = useCallback(async () => {
    if (loaded) return;
    if (inflight.current) return inflight.current;

    inflight.current = Promise.allSettled([
      getAllEntreprises(),
      getAllPartners(),
      getAllClientRequests(),
    ]).then((results) => {
      const [c, p, r] = results;
      if (c.status === 'fulfilled') {
        setClients(Array.isArray(c.value) ? c.value : c.value?.data || []);
      }
      if (p.status === 'fulfilled') {
        setPartners(Array.isArray(p.value) ? p.value : p.value?.data || []);
      }
      if (r.status === 'fulfilled') {
        setRequests(Array.isArray(r.value) ? r.value : r.value?.data || []);
      }
      setLoaded(true);
      inflight.current = null;
    });

    return inflight.current;
  }, [loaded]);

  return { clients, partners, requests, loaded, ensureLoaded };
}

export default useEntityCommands;
